export interface BoardTab<T extends string> {
  id: T;
  label: string;
}

interface BoardTabsProps<T extends string> {
  tabs: BoardTab<T>[];
  active: T;
  onChange: (tab: T) => void;
  ariaLabel: string;
}

/**
 * Top-level tab switcher for a board page (e.g. Projects' Current/Archived,
 * Makers' Makers/Availability). Rendered as the first child of the board so it
 * sits in normal flow; any controls row below it keeps its own sticky/floating
 * behavior.
 */
export function BoardTabs<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
}: BoardTabsProps<T>) {
  // Plain buttons rather than the ARIA tablist/tab pattern: these switch a view
  // via a query param and have no tabpanel/roving-tabIndex/arrow-key wiring, so
  // tab roles would over-promise to assistive tech. aria-pressed conveys the
  // selected state honestly with native button keyboard behavior.
  return (
    <nav className="board-tabs" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          aria-pressed={active === tab.id}
          className={`board-tabs__tab ${active === tab.id ? "board-tabs__tab--active" : ""}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}
